import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../hooks/useTranslation';
import { StoreState } from '../../store/createStore';
import Input from '../Input';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { ReactCreditCardProps } from '../../store/modules/auth/types';

import {
  ContainerPaymentData,
  ContainerDataRequest,
  ContainerDataRequestItem,
  ContainerDefaultPayCard,
  CardNumber,
  CardName,
  ContainerCardCredit,
  ContainerFormCardCredit,
  FormCardCreditBox,
  ContainerCreate,
  ContainerSectionFooter,
  ContainerButtons,
  Button,
} from './styles';
import getValidationErrors from '../../utils/getValidationErrors';
import { openShowModal } from '../../store/modules/parameter/actions';
import { sendRequestNew } from '../../store/modules/checkout/actions';
import ReactCreditCard from '../ReactCreditCard';
import { CreditCardHelper } from '../../utils/creditCardHelper';

interface SubmitProps {
  id?: number;
  number?: string;
  expiration?: string;
  name?: string;
  cvc?: string;
}

const PaymentConfirmCVV: React.FC = () => {
  const dispatch = useDispatch();
  const [values, setValues] = useState<ReactCreditCardProps>(
    {} as ReactCreditCardProps,
  );
  const [focused, setFocus] = useState<string | undefined>('cvc');
  const formRef = useRef<FormHandles>(null);
  const { onTranslation: t } = useTranslation();
  const { checkout, shipping } = useSelector((state: StoreState) => state.checkout);
  const { store, locker, address, method, selectedShippingOption } = useSelector(
    (state: StoreState) => state.parameter,
  );
  const {
    user: { data },
  } = useSelector((state: StoreState) => state.auth);

  const handledSubmit = useCallback(
    async (data: SubmitProps) => {
      try {
        const schema = Yup.object().shape({
          cvc: Yup.string()
            .transform((value: string) => {
              return value.split(/\D/).join('');
            })
            .when(['valueEdit'], {
              is: true, // alternatively: (isBig, isSpecial) => isBig && isSpecial
              then: Yup.string().required(
                `${t('main.messages_yup.cvc_card_required')}`,
              ),
            }),
        });

        await schema.validate(
          {
            ...data,
            valueEdit: values.id > 0 ? false : true,
          },
          {
            abortEarly: false,
          },
        );
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
          //konsole.log('handleSubmit errors:', errors);
        }
      }
    },
    [values.id, t],
  );

  const handledBlur = () => setFocus(undefined);
  const handledFocus = useCallback(
    event => setFocus(event.target.name as string),
    [setFocus],
  );

  const handledChange = useCallback(
    event => {
      let valueStr = '';
      const { name, value } = event.target;
      switch (name) {
        case 'number': {
          valueStr = value.replace(/\D/g, '');
          break;
        }
        default: {
          valueStr = value;
        }
      }
      setValues(v => ({ ...v, [name]: valueStr }));
    },
    [setValues],
  );

  const handledCLose = useCallback(() => {
    dispatch(openShowModal(false));
  }, [dispatch]);

  const handledFinish = useCallback(() => {
    dispatch(openShowModal(false));
    if (data.seller === 1 && checkout.customer?.data.customer) {
      dispatch(
        sendRequestNew({
          email: checkout.customer?.data.customer.email,
          checkout: {
            ...checkout,
            shipping
          },
          store: store,
          address: address,
          locker: locker,
          method: method,
          sellerId: data.customer_id,
          security_code: values.cvc,
          shippingMethod: selectedShippingOption?.carrier,
          shippingDate:  selectedShippingOption?.chosen_shipping_date,
          windowsConfig: {
            width: window.screen.width,
            height: window.screen.height,
            colorDepth: window.screen.colorDepth
          }
        }),
      );
    } else if (data.email) {
      dispatch(
        sendRequestNew({
          email: data.email,
          checkout: {
            ...checkout,
            shipping
          },
          store,
          address,
          locker,
          method,
          security_code: values.cvc,
          shippingMethod: selectedShippingOption?.carrier,
          shippingDate:  selectedShippingOption?.chosen_shipping_date,
          windowsConfig: {
            width: window.screen.width,
            height: window.screen.height,
            colorDepth: window.screen.colorDepth
          }
        })
      );
    }
  }, [
    values, 
    address, 
    checkout,
    shipping,
    data.customer_id, 
    data.email, 
    data.seller, 
    dispatch, 
    locker,
    method, 
    store,
    selectedShippingOption]);
  useEffect(() => {
    if (method) {
      setValues({
        id: 0,
        cvc: '',
        name: method.full_name ? method.full_name : '',
        expiration: method.expiration ? method.expiration : '',
        number: method.card_number ? method.card_number : '',
        cardLastFour: method.card_last_four ? method.card_last_four : '',
        customer_doc: method.customer_doc ? method.customer_doc : '',
        type: method.card_brand ? method.card_brand : '',
      });
    }
  }, [method]);

  return (
    <>
      {method && (
        <>
          <ContainerPaymentData>
            <ContainerDataRequest>
              <ContainerDataRequestItem>
                <h4>{t('main.new_checkout.view_payment.method_selected')}</h4>
              </ContainerDataRequestItem>

              <ContainerDataRequestItem>
                <ContainerDefaultPayCard>
                  <CardNumber>
                    <span>
                      {'****.****.****.' + method.card_last_four}
                    </span>
                  </CardNumber>
                  <CardName>
                    <span>{method.full_name}</span>
                  </CardName>
                </ContainerDefaultPayCard>
              </ContainerDataRequestItem>

              <ContainerDataRequestItem>
                <ContainerCreate>
                  <ContainerCardCredit>
                    <ReactCreditCard
                      {...values}
                      focused={focused}
                      placeholderName={`${t(
                        'main.component_account_payment.payment_create.cardholder_name'
                      )}`}
                    />
                  </ContainerCardCredit>
                  <ContainerFormCardCredit>
                    <Form
                      // initialData={{ postcode: '06422070' }}
                      ref={formRef}
                      onSubmit={handledSubmit}
                    >
                      <FormCardCreditBox bDirectionColumn>
                        <Input
                          mask={"#".repeat(CreditCardHelper.getCardInfo({
                              type: method.card_brand,
                            }).cvcLength)}
                          name="cvc"
                          textLabel={`${t(
                            'main.component_account_payment.payment_create.input_cvc'
                          )}`}
                          Border="1px solid var(--primary)"
                          formRef={formRef}
                          handledInputBlurExt={handledBlur}
                          handledInputFocusExt={handledFocus}
                          onChangeExt={handledChange}
                          disabled={false}
                        />
                      </FormCardCreditBox>
                    </Form>
                  </ContainerFormCardCredit>
                </ContainerCreate>
              </ContainerDataRequestItem>
            </ContainerDataRequest>
          </ContainerPaymentData>
          <ContainerSectionFooter>
            <ContainerButtons>
              <Button
                id="button-action-close-cvc"
                onClick={handledCLose}
                bgColor="#e22301"
                fgColor="#FFFFFF"
              >
                <span>{t('main.new_checkout.close_cvv')}</span>
              </Button>
              <Button
                id="button-action-confirm-cvc"
                onClick={handledFinish}
                bgColor="#00b300"
                fgColor="#FFFFFF"
              >
                <span>{t('main.new_checkout.finish_payment')}</span>
              </Button>
            </ContainerButtons>
          </ContainerSectionFooter>
        </>
      )}
    </>
  );
};

export default PaymentConfirmCVV;
